/*
 * <klaviyo-form> — themed email capture that submits to Klaviyo's browser-safe
 * Client Subscriptions API, keeping the user on-page and toggling an inline
 * success / error state. Falls back to the native form post when JS is off or
 * the company / list id isn't configured.
 *
 * Shared by sections/newsletter.liquid and sections/footer.liquid. Expects:
 *   data-endpoint     Client API base, e.g. https://a.klaviyo.com/client/subscriptions/
 *   data-list-id      Klaviyo list id (the form/list to subscribe to)
 *   data-account-id   Klaviyo public API key (company id)
 * and child hooks: [data-klaviyo-form-el] [data-klaviyo-email] [data-klaviyo-submit]
 *   [data-klaviyo-error] [data-klaviyo-success]
 */
if (!customElements.get('klaviyo-form')) {
  class KlaviyoForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('[data-klaviyo-form-el]');
      this.emailEl = this.querySelector('[data-klaviyo-email]');
      this.submitEl = this.querySelector('[data-klaviyo-submit]');
      this.errorEl = this.querySelector('[data-klaviyo-error]');
      this.successEl = this.querySelector('[data-klaviyo-success]');
      this.endpoint = this.dataset.endpoint;
      this.listId = this.dataset.listId;
      this.companyId = this.dataset.accountId;
      if (this.form) this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      // No company/list id configured: let the native form post happen.
      if (!this.companyId || !this.listId) return;
      event.preventDefault();

      const email = (this.emailEl.value || '').trim();
      if (!email) return;

      this.setError('');
      this.submitEl.disabled = true;

      const url = this.endpoint + '?company_id=' + encodeURIComponent(this.companyId);
      const payload = {
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: { email: email } } },
          },
          relationships: { list: { data: { type: 'list', id: this.listId } } },
        },
      };

      try {
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify(payload),
        });
        // The Client API returns 202 with an empty body on success.
        if (res.ok) {
          this.form.classList.add('hidden');
          if (this.successEl) this.successEl.classList.remove('hidden');
          return;
        }
        let message = '';
        try {
          const data = await res.json();
          if (data && data.errors && data.errors.length) message = data.errors[0].detail || data.errors[0].title;
        } catch (e) {}
        this.setError(message || 'Something went wrong. Please try again.');
      } catch (e) {
        this.setError('Something went wrong. Please try again.');
      } finally {
        this.submitEl.disabled = false;
      }
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.toggle('hidden', !message);
    }
  }
  customElements.define('klaviyo-form', KlaviyoForm);
}
